import { useQuery } from '@tanstack/react-query';
import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import * as services from '../../../../../components/FilterSection/services';
import { AsyncCheckPillProps } from '../../../../../components/Filters';
import { checkIfFiltersValuesIsEmpty } from '../../../../../utils';
import { useCampanhaStore } from '../../store/campanha-store';
import { useOfferStore } from '../../store/offer-store';
import { listBlocos } from '../BlocoDeOferta/services';

export type FilterObject<K extends string = string> = {
  dataKey: K;
  groupBy?: string;
} & AsyncCheckPillProps;

type FILTER_KEYS = 'productIds' | 'productFamilyIds' | 'segmentation' | 'offerBlock' | 'offerType' | string;

type Queries = FilterObject<FILTER_KEYS>[];

export const useOfertasFilters = () => {
  const { id } = useParams<{ id: string }>();
  const { updateOfertasFilters, ofertasFilters } = useCampanhaStore(id);
  const { offerTypes } = useOfferStore();

  const set = useCallback(
    (key: FILTER_KEYS, value: string[]) => {
      updateOfertasFilters(key, value);
    },
    [updateOfertasFilters],
  );

  const handleClean = useCallback(
    (key: FILTER_KEYS) => {
      set(key, []);
    },
    [set],
  );

  const queries: Queries = React.useMemo(
    () => [
      {
        dataKey: 'productIds',
        placeholder: 'Produto',
        queryFn: services.listProducts,
        value: ofertasFilters.productIds,
        onSelect: value => set('productIds', value),
        onClean: () => handleClean('productIds'),
      },
      {
        dataKey: 'productFamilyIds',
        placeholder: 'Família',
        queryFn: services.listFamilies,
        value: ofertasFilters.productFamilyIds,
        onSelect: value => set('productFamilyIds', value),
        onClean: () => handleClean('productFamilyIds'),
      },
      // Fornecedor e Marca não estão sendo usados no momento
      // {
      //   dataKey: 'supplier',
      //   placeholder: 'Fornecedor',
      //   queryFn: services.listSuppliers,
      //   value: ofertasFilters.supplier,
      //   onSelect: value => set('supplier', value),
      //   onClean: () => handleClean('supplier'),
      // },
      // {
      //   dataKey: 'productBrand',
      //   placeholder: 'Marca',
      //   queryFn: services.listProductBrands,
      //   value: ofertasFilters.productBrand,
      //   onSelect: value => set('productBrand', value),
      //   onClean: () => handleClean('productBrand'),
      // },
      {
        dataKey: 'offerBlock',
        placeholder: 'Bloco de oferta',
        queryFn: () => listBlocos(id, { size: 1000 }).then(resp => resp?.content?.map((item: any) => ({ value: item.name, label: item.name })) || []),
        value: ofertasFilters.offerBlock,
        onSelect: value => set('offerBlock', value),
        onClean: () => handleClean('offerBlock'),
        groupBy: '',
      },
      {
        dataKey: 'offerType',
        placeholder: 'Tipo de oferta',
        queryFn: () => Promise.resolve(offerTypes),
        value: ofertasFilters.offerType,
        onSelect: value => set('offerType', value),
        onClean: () => handleClean('offerType'),
      },
    ],
    [ofertasFilters, set],
  );

  const { data: segments } = useQuery({
    queryKey: ['list-segments'],
    queryFn: services.listaSegmentos,
    initialData: [],
  });

  const segmentsQueries: Queries = React.useMemo(() => {
    return segments.map(segment => ({
      dataKey: segment.field,
      placeholder: segment.name,
      value: ofertasFilters[segment.field],
      queryFn: query => services.listSegment(query, segment.level),
      level: segment.level,
      onSelect: value => set(segment.field, value),
      onClean: () => handleClean(segment.field),
    }));
  }, [segments, ofertasFilters, set]);

  const filtersWithSegments = React.useMemo(() => [...queries, ...segmentsQueries], [queries, segmentsQueries]);

  const memoValues = React.useMemo(() => ofertasFilters, [ofertasFilters]);

  const isEmpty = checkIfFiltersValuesIsEmpty(ofertasFilters);

  return {
    list: filtersWithSegments,
    values: memoValues,
    isEmpty,
  } as const;
};
